import React, { useState, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import InlineAttachment from '@/shared/components/InlineAttachment';
import Checkbox from '@/shared/components/input/Checkbox';
import messages from './interactionMessages';

const AttachmentSelectionModal = ({
  show,
  attachments = [],
  onClose,
  onConfirm,
  intl,
  title,
  description,
  disabled,
}) => {
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleAttachmentToggle = useCallback(
    (attachmentId, newValue) => {
      setSelectedAttachmentIds(prevSelectedIds => {
        let newSelectedIds;
        if (newValue) {
          // Add attachment if not already selected
          newSelectedIds = prevSelectedIds.includes(attachmentId)
            ? prevSelectedIds
            : [...prevSelectedIds, attachmentId];
        } else {
          // Remove attachment
          newSelectedIds = prevSelectedIds.filter(id => id !== attachmentId);
        }

        // Update selectAll state based on new selection
        const newSelectAll = newSelectedIds.length === attachments.length;
        setSelectAll(newSelectAll);

        return newSelectedIds;
      });
    },
    [attachments.length],
  );

  const handleSelectAllToggle = useCallback(
    newValue => {
      setSelectAll(newValue);
      setSelectedAttachmentIds(newValue ? attachments.map(att => att.id) : []);
    },
    [attachments],
  );

  const handleConfirm = useCallback(() => {
    onConfirm(selectedAttachmentIds);
  }, [onConfirm, selectedAttachmentIds]);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const selectedCount = selectedAttachmentIds.length;

  return (
    <OkCancelModal
      show={show}
      title={title}
      onClose={onClose}
      onSave={handleConfirm}
      disabled={disabled}
      cancelDisabled={disabled}
      okLabel={intl.formatMessage(messages.Yes)}
    >
      <div>
        <p className="margin10B">{description}</p>

        <div className="attachment-selection-list">
          {attachments.length > 1 && (
            <div className="attachment-selection-item margin10B">
              <div className="flex-row">
                <div className="flex-item-fixed margin10R">
                  <Checkbox
                    id="selectAllAttachments"
                    value={selectAll}
                    onClick={handleSelectAllToggle}
                  />
                </div>
                <div className="flex-item">
                  <strong>{intl.formatMessage(messages.IncludeAllAttachments)}</strong>
                </div>
              </div>
            </div>
          )}

          {attachments.map(attachment => (
            <div key={attachment.id} className="attachment-selection-item margin10B">
              <div className="flex-row">
                <div className="flex-item-fixed margin10R">
                  <Checkbox
                    id={`attachment-${attachment.id}`}
                    value={selectedAttachmentIds.includes(attachment.id)}
                    onClick={newValue => handleAttachmentToggle(attachment.id, newValue)}
                    disabled={disabled}
                  />
                </div>
                <div className="flex-item">
                  <InlineAttachment
                    attachment={attachment}
                    showDeleteConfirmation={false}
                    onDelete={null}
                    noDownload={true}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </OkCancelModal>
  );
};

export default injectIntl(AttachmentSelectionModal);
